import React, { memo, useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DesktopTagRow } from './DesktopTagRow';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

export interface SortableTagListProps {
  tags: CorkBoardTag[];
  editingTagId: number | null;
  onToggleEnabled: (tag: CorkBoardTag) => void;
  onEdit: (tag: CorkBoardTag) => void;
  onDelete: (tag: CorkBoardTag) => void;
  onReorder: (next: CorkBoardTag[]) => void;
}

interface SortableRowProps {
  tag: CorkBoardTag;
  isEditing: boolean;
  onToggleEnabled: (tag: CorkBoardTag) => void;
  onEdit: (tag: CorkBoardTag) => void;
  onDelete: (tag: CorkBoardTag) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  tag,
  isEditing,
  onToggleEnabled,
  onEdit,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tag.id,
  });

  // .web.tsx file — using a vanilla <div> as the sortable wrapper avoids the
  // RN <View> ref-type mismatch that dnd-kit's setNodeRef expects an HTMLElement.
  // DesktopTagRow renders inside this wrapper so its layout/styles are unchanged.
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0,
      }}
    >
      <DesktopTagRow
        tag={tag}
        isEditing={isEditing}
        onToggleEnabled={onToggleEnabled}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

export const SortableTagList: React.FC<SortableTagListProps> = memo(
  ({ tags, editingTagId, onToggleEnabled, onEdit, onDelete, onReorder }) => {
    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const ids = useMemo(() => tags.map((t) => t.id), [tags]);

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = tags.findIndex((t) => t.id === active.id);
        const newIndex = tags.findIndex((t) => t.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onReorder(arrayMove(tags, oldIndex, newIndex));
      },
      [tags, onReorder],
    );

    return (
      <View style={styles.container}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {tags.map((tag) => (
              <SortableRow
                key={tag.id}
                tag={tag}
                isEditing={tag.id === editingTagId}
                onToggleEnabled={onToggleEnabled}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      </View>
    );
  },
);

SortableTagList.displayName = 'SortableTagList';

const styles = StyleSheet.create({
  container: {
    padding: 8,
  },
});
